import React from 'react';
import { BookOpenIcon, CreditCardIcon, LockKeyholeIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CTAButton } from './Shared';
import { HerbSprig, LeafCluster, Mandala, MortarPestle } from './HealingIcons';
const productMockupUrl = 'https://cdn.magicpatterns.com/uploads/mWTuvE47PoqkYMryatpyWz/Mockup_Botiquin_Ancestral.png';
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <header className="relative isolate overflow-hidden pb-8 pt-3 sm:pt-7 md:pb-14 md:pt-16" data-id="element-137" data-mp="xa4h5bx7i9f2">
      <Mandala className="pointer-events-none absolute -right-20 top-4 -z-10 h-72 w-72 opacity-[0.11] md:right-6 md:h-96 md:w-96" color="#B88A35" data-id="element-138" data-mp="sart4nulmwaw" />
      <Mandala className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-80 w-80 opacity-[0.09] md:h-[30rem] md:w-[30rem]" color="#2E5946" data-id="element-139" data-mp="nekqp51ew2xp" />
      <HerbSprig className="absolute left-[7%] top-48 hidden h-14 w-14 opacity-35 md:block" color="#647D56" data-id="element-140" data-mp="3814alm6t1smr" />
      <LeafCluster className="absolute right-[7%] top-56 hidden h-16 w-16 opacity-30 md:block" color="#2E5946" data-id="element-141" data-mp="3o7lqjmmw8ymu" />

      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-142" data-mp="399ba3kjqp7ue">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-143" data-mp="3e963pvuwgbj5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-brand-gold/55 bg-brand-paper px-3 py-1 shadow-sm sm:mb-5 sm:px-4 sm:py-1.5" data-id="element-144" data-mp="2viy1rh2xrr4j">
            <HerbSprig className="h-3.5 w-3.5 sm:h-4 sm:w-4" color="#2E5946" data-id="element-145" data-mp="2hjoux80me9d8" />
            <span className="font-serif text-[10px] font-semibold tracking-[0.16em] text-brand-ink sm:text-xs" data-id="element-146" data-mp="2ljpef72ktar5">
              LUMEN AKASHA™
            </span>
          </div>

          <p className="mb-1 font-serif font-semibold uppercase tracking-[0.16em] text-brand-forest sm:mb-3 sm:text-base sm:tracking-[0.18em] md:text-lg text-[18px]" data-id="element-147" data-mp="nat65811aej4">
            El Botiquín Ancestral™
          </p>
          <h1 className="mx-auto max-w-5xl font-display text-[35px] font-bold leading-[0.72] text-brand-ink sm:text-5xl sm:leading-[0.86] md:text-7xl md:leading-[0.9]" data-id="element-148" data-mp="de6oes4083lb">
            <span className="block" data-id="element-149" data-mp="1622irq63pjex">GUÍA VISUAL DE</span>
            <span className="relative z-0 inline-block px-1.5 text-brand-purpleDark sm:px-3" data-id="element-150" data-mp="162m137zny9th">
              <span className="absolute inset-x-0 bottom-[0.04em] -z-10 h-[0.48em] -rotate-1 rounded-sm bg-brand-goldLight/90" aria-hidden="true" data-id="element-151" data-mp="111525i0qoxfj" />
              DIGITOPUNTURA
            </span>
            <span className="block" data-id="element-152" data-mp="3hrd9kw2ghc0f">PARA EL ALIVIO INMEDIATO</span>
          </h1>
          <p className="mx-auto mt-2 max-w-xs font-serif text-[20px] font-semibold leading-[1.05] text-brand-forest sm:hidden" data-id="element-153" data-mp="3htg5txu7c1dl">
            50 puntos esenciales para aliviar los síntomas más frecuentes.
          </p>
          <p className="mx-auto mt-2 hidden max-w-3xl text-base leading-relaxed text-brand-text sm:block md:mt-5 md:text-xl" data-id="element-154" data-mp="269zjnwpy93pz">
            El conocimiento ancestral ya existe. La herramienta visual para
            aplicarlo en segundos, ahora también.
          </p>

          <div className="relative mx-auto mt-3 w-[calc(100%+2rem)] max-w-[34rem] -translate-x-4 sm:mt-8 sm:w-full sm:max-w-4xl sm:translate-x-0" data-id="element-155" data-mp="2fzj3b4lhl1lw">
            <MortarPestle className="absolute -left-5 top-1/2 hidden h-12 w-12 -translate-y-1/2 opacity-50 md:block" color="#B66735" data-id="element-156" data-mp="2ozz4u8io5yo4" />
            <motion.img src={productMockupUrl} alt="Mockup de El Botiquín Ancestral con ebook, fichas visuales y seis bonos de regalo" initial={reduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: reduceMotion ? 0 : 0.12,
            ease: [0.22, 1, 0.36, 1]
          }} className="mx-auto h-auto max-h-[43svh] w-full object-contain drop-shadow-[0_18px_22px_rgba(34,54,70,0.22)] sm:max-h-[42rem] sm:max-w-3xl md:max-w-4xl" draggable={false} decoding="async" data-id="element-157" data-mp="umhc873eqbm8" />
          </div>

          <div className="mt-5 flex justify-center sm:hidden" data-id="element-158" data-mp="3rhwtzckrbsgx">
            <CTAButton className="w-full max-w-[22rem] px-5 py-3 text-[13px] leading-tight" data-id="element-159" data-mp="3mollz847l5nf">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
          </div>

          <div className="mx-auto mt-8 hidden max-w-3xl space-y-3 px-2 sm:block" data-id="element-160" data-mp="2tzn0c10vnldd">
            <h2 className="font-serif text-xl font-bold uppercase tracking-wide text-brand-forest md:text-3xl" data-id="element-161" data-mp="19i5ekisgb8h5">
              EL BOTIQUÍN ANCESTRAL™
            </h2>
            <p className="font-serif text-base font-semibold text-brand-text md:text-xl" data-id="element-162" data-mp="1eb6yx6bfg084">
              50 puntos esenciales para aliviar los síntomas más frecuentes.
            </p>
            <p className="text-sm text-brand-muted md:text-base" data-id="element-163" data-mp="14ju88i1d7g3o">
              Tu guía visual de digitopuntura para el alivio inmediato.
            </p>
          </div>

          <div className="mt-7 hidden flex-col items-center gap-5 sm:flex" data-id="element-164" data-mp="1x76oybkugtp2">
            <CTAButton className="px-7 py-4 text-sm md:px-10 md:text-lg" data-id="element-165" data-mp="1j9035tt66k5r">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
            <div className="flex max-w-3xl flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-brand-muted md:text-sm" data-id="element-166" data-mp="1n88xp0w1am2d">
              <span className="inline-flex items-center gap-1.5" data-id="element-167" data-mp="buwyx4f4stn2">
                <ShieldCheckIcon className="h-4 w-4 text-brand-forest" data-id="element-168" data-mp="bwgcuodbeslo" />
                Garantía 7 días
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-169" data-mp="2ejepnug8ex1f">
                <BookOpenIcon className="h-4 w-4 text-brand-forest" data-id="element-170" data-mp="29izglf2zgbp9" />
                Listo para usar en tablet o imprimir
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-171" data-mp="1zsnymomgs1zd">
                <LockKeyholeIcon className="h-4 w-4 text-brand-forest" data-id="element-172" data-mp="1zse8n11f5uto" />
                Pago seguro
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-173" data-mp="1voq1ukkm339h">
                <CreditCardIcon className="h-4 w-4 text-brand-forest" data-id="element-174" data-mp="1qqer87mhsoq7" />
                Mercado Pago
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold text-brand-ink md:text-sm" data-id="element-175" data-mp="1gttl1iyql7ks">
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-176" data-mp="3g7fsz7367jbb">
                <BookOpenIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-177" data-mp="3b7bbbdqj0iji" />
                100% PDF
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-178" data-mp="3jo27srnmfizh">
                <ZapIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-179" data-mp="3np45h41w3mzc" />
                Acceso en 5 min
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-180" data-mp="3sjhv0ptvezw3">
                <SparklesIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-181" data-mp="1cow5smjvcuq" />
                Para todos los niveles
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </header>;
}